import React, { useState, useCallback, useEffect } from "react";
import { Box, Skeleton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Tooltip,
  ReferenceLine,
  Cell,
} from "recharts";
import styles from "./CustomChart.module.css";
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const value = Math.ceil(payload[0].value);

  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatDate = (val) => {
    const date = new Date(val);
    return isNaN(date)
      ? (val ?? "-")
      : date.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        p: 1.5,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        minWidth: 200,
      }}
    >
      {/* Title */}
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>{formatKey(payload[0].name)}</Box>

      {/* Value */}
      <Box sx={{ fontSize: 13, mb: 0.5 }}>Value: {value}</Box>

      {/* Additional Fields */}
      {data?.additional &&
        Object.entries(data.additional).map(([key, val]) => (
          <Box
            key={key}
            sx={{
              fontSize: 12,
              color: "#6B7280",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>{formatKey(key)}</span>
            <span>{formatDate(val)}</span>
          </Box>
        ))}
    </Box>
  );
};
const CustomLabel = ({ x, y, width, index, data, valueColor, smallBarColor }) => {
  const item = data[index];
  if (!item) return null;

  const isSmallBar = width < 80;

  // const additionalValues =
  //   item?.additional && typeof item.additional === "object"
  //     ? Object.values(item.additional).join(", ")
  //     : "";

  const textX = isSmallBar ? x + width + 10 : x + width - 10;
  const textAnchor = isSmallBar ? "start" : "end";

  return (
    <text
      x={textX}
      y={y + 18}
      textAnchor={textAnchor}
      fontSize={14}
      fontWeight={600}
      fill={isSmallBar ? smallBarColor : valueColor}
    >
      {/* <tspan x={textX} dy="0">
        {item.value}
      </tspan>

      {additionalValues && (
        <tspan
          x={textX}
          dy="1.2em"
          fontSize={12}
          fontWeight={400}
          fill={isSmallBar ? smallBarColor : valueColor}
        >
          ({additionalValues})
        </tspan>
      )} */}
    </text>
  );
};;

const HorizontalBarChart = ({
  data = [],
  barColor = "#B6F7FF",
  valueColor = "#1A2025",
  percentColor = "#667F93",
  smallBarColor = "#0066CC",
  refreshTrigger,
  name,
  value,
  threshold,
  thresholdColor = "#FF4D4F",
  aboveThresholdColor,
  belowThresholdColor,
}) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(data);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setChartData([...data]);
      setLoading(false);
    }, 1500);
  }, [data]);

  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  const hasThreshold = threshold != null && !isNaN(Number(threshold));
  const aboveColor = aboveThresholdColor || thresholdColor;
  const belowColor = belowThresholdColor || barColor;

  const renderChart = useCallback(
    (height = { sm: 160, md: 200, lg: 300 }) => {
      const barHeight = 42;
      const calculatedHeight = chartData?.length * barHeight;

      if (loading) {
        return (
          <Box sx={{ px: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Skeleton variant="text" width={100} height={18} />
                <Skeleton variant="rounded" height={24} />
              </Box>
            ))}
          </Box>
        );
      }

      if (!chartData || chartData.length === 0) {
        return (
          <Box
            sx={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9CA3AF",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            No data available
          </Box>
        );
      }

      return (
        <Box
          sx={{
            height,
            overflowY: "auto",
            px: 1,
            py: 1,
            borderRadius: "12px",
          }}
        >
          <Box
            sx={{
              height: Math.max(calculatedHeight, height),
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 8, right: 20, left: 10, bottom: 8 }}
                barCategoryGap="20%"
              >
                <CartesianGrid stroke="#E5E7EB" horizontal={false} vertical />

                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickMargin={10}
                />

                <YAxis
                  type="category"
                  dataKey={name}
                  width={140}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "#374151",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
                <Tooltip content={<CustomTooltip />} />

                {hasThreshold && (
                  <ReferenceLine
                    x={Number(threshold)}
                    stroke={thresholdColor}
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    label={{
                      value: `Threshold: ${threshold}`,
                      position: "insideTopRight",
                      fill: thresholdColor,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                )}

                <Bar
                  dataKey={value}
                  fill={barColor}
                  radius={[0, 10, 10, 0]}
                  barSize={28}
                >
                  {hasThreshold &&
                    chartData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={
                          Number(entry[value]) >= Number(threshold)
                            ? aboveColor
                            : belowColor
                        }
                      />
                    ))}
                  <LabelList
                    dataKey={value}
                    content={(props) => (
                      <CustomLabel
                        {...props}
                        data={chartData}
                        valueColor={valueColor}
                        percentColor={percentColor}
                        smallBarColor={smallBarColor}
                      />
                    )}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      );
    },
    [
      loading,
      chartData,
      barColor,
      valueColor,
      percentColor,
      smallBarColor,
      hasThreshold,
      threshold,
      thresholdColor,
      aboveColor,
      belowColor,
    ],
  );

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 4,
      }}
    >
      <Box className={styles.graphBG}>
        {renderChart({ sm: 160, md: 200, lg: 300 })}
      </Box>
    </Box>
  );
};

export default HorizontalBarChart;
